/* ------------------------------------------------------------------ */
/*  Create Experiment Wizard — Types                                  */
/* ------------------------------------------------------------------ */

import type {
  ArmCombination,
  VariantArmId,
} from '~/features/configuration/types/treatment-configuration.types';
import {
  armsForCombination,
  variantArmsForCombination,
} from '~/features/configuration/utils/arm-combinations';
import type { EnrichmentPostStepFlags } from '~/types/enrichment-flags';

import type { AppliedProductFilters } from './product-metadata.types';

// ---- Step 1 --------------------------------------------------------

/**
 * Grouping-method code — sourced directly from the backend
 * `/connectors/default-grouping-config` response (e.g. `ITEM_GROUP_ID`, `MPN`,
 * `OFFER_ID`, `MANUAL`). Typed as `string` so new backend codes work without
 * a frontend release; consumers that need method-specific UI copy match on
 * the known codes and fall back to the API-provided description.
 */
export type GroupingMethod = string;

/** Sentinel value for the "manual" flow — matches backend enum casing. */
export const MANUAL_GROUPING_METHOD = 'MANUAL';

/**
 * Which identifier a candidate-scope CSV holds. Deliberately the same codes as
 * `GroupingMethod` so the two can be compared directly — an experiment grouped
 * by `ITEM_GROUP_ID` whose scope file lists offer ids is almost always a
 * mistake, and the UI can only warn about it if the vocabularies match.
 */
export type CandidateIdType = 'OFFER_ID' | 'ITEM_GROUP_ID';

export interface SampleProduct {
  id: string;
  title: string;
  productId: string;
  countries: string;
  availability: 'in_stock' | 'out_of_stock' | 'preorder';
  offerId?: string;
  mpn: string;
  gtin: string;
  itemGroupId: string;
  imageUrl: string;
}

export interface ProductPreview {
  parentProduct: SampleProduct;
  variants: SampleProduct[];
}

export interface Step1State {
  method: GroupingMethod | null;
  /** Free-form rule entered by the user when method === 'manual'. */
  manualRule: string;
  previewFetched: boolean;
}

// ---- Step 2 --------------------------------------------------------

export interface CatalogProduct {
  id: string;
  title: string;
  productId: string;
  countries: string;
  availability: 'in_stock' | 'out_of_stock' | 'preorder';
  mpn: string;
  gtin: string;
  itemGroupId: string;
  imageUrl: string;
  priceCurrency: string;
  channel: string;
  creationTime: string;
  dataSource: string;
  expirationTime: string;
  googleProductCategory: string;
  /** Index signature — allows the table to access arbitrary dynamic columns from the raw API response via `resolveProductValue`. */
  [key: string]: unknown;
}

/**
 * Legacy column id set — kept so the step-2 / step-3 data tables continue to
 * show the same 7 columns until the product-list API starts returning the
 * missing fields (countries/mpn/gtin/itemGroupId). Once that lands, the
 * backend-driven `ProductColumn.code` values will replace this.
 */
export const CATALOG_COLUMNS = ['title', 'productId', 'availability', 'priceCurrency'] as const;

export type CatalogColumn = (typeof CATALOG_COLUMNS)[number];

/** Default additional (non-pinned) column codes shown on first mount. Target country is on by
 *  default because one catalog can carry several markets; the rest are opt-in via Manage Columns. */
export const DEFAULT_VISIBLE_COLUMNS: string[] = ['target_country'];

export interface ProductGroup {
  id: string;
  name: string;
  /** Snapshot of step-3 filters at the moment the group was created — sent to the backend on Next. */
  filters: AppliedProductFilters;
  /** Snapshot of the merged-filter product count at creation time. Drives the group's display + "All Other" arithmetic. */
  skuCount: number;
}

export type GroupingMode = 'custom' | 'none';

/** Step 2 — Define Scope: catalog filters, search, pagination, column visibility. */
export interface Step2State {
  search: string;
  page: number;
  pageSize: number;
  /** Keyed by the filter `key` returned by `POST /products/filters`; value shape depends on the filter type. */
  appliedFilters: AppliedProductFilters;
  /** Columns selected in the Manage Columns drawer. Values are API column codes (from `GET /products/columns`). */
  visibleColumns: string[];
  /** Which identifier the NEXT upload will be read as — the toggle's value.
   *  Defaults from the experiment's grouping method, so the common case needs no
   *  thought. Changing it never touches an existing scope; uploading replaces. */
  uploadIdType: CandidateIdType;
  /** Which identifier the STORED scope holds, from the upload response or
   *  `offerIdsType` on resume. `null` when no scope is applied. Kept apart from
   *  `uploadIdType` so the count is labelled by what was actually uploaded, not
   *  by wherever the toggle happens to be pointing. */
  uploadedIdType: CandidateIdType | null;
  /** How many unique ids the candidate file carries — from the local parse on
   *  upload, or the server's `offerIdsTotal` on resume. `null` when no list is
   *  applied. The TRUE total, even when more ids were parsed than are kept
   *  below. */
  uploadCount: number | null;
  /**
   * Ids kept to narrow the PREVIEW table, capped at
   * `PRODUCT_FILTER_VALUE_LIMIT` — the product-list endpoint 422s past that, and
   * a bigger array would bloat both sessionStorage and every request body. Empty
   * when the file is larger than the cap (the preview then stays unnarrowed) or
   * on resume, where only the count survives.
   *
   * NOT the scope: the uploaded file is, and it is applied server-side at content
   * scoring. These only decide what the operator is shown here.
   */
  uploadPreviewIds: string[];
  /** File name of the uploaded candidate list — shown as a summary on re-entry. */
  uploadFileName: string | null;
}

/** Step 3 — Analytics Setup: custom grouping vs no grouping, with at most one group. */
export interface Step3State {
  mode: GroupingMode | null;
  group: ProductGroup | null;
  /**
   * Additional filters layered on top of step-2's locked `appliedFilters`.
   * Step 2's filters are read-only here — step 3 can refine the scope but
   * not modify the upstream selection. The product-list call merges both;
   * the chip row + drawer only reflect this slice.
   */
  appliedFilters: AppliedProductFilters;
}

// ---- Step 4 --------------------------------------------------------

export interface KpiOption {
  value: string;
  label: string;
  category: string;
  /** Optional secondary line (e.g. underlying metric / data source) shown in Review & Launch summaries. */
  subLabel?: string;
}

/**
 * Traffic-split percentages for the experiment, **positional against the arms
 * the combination runs** (see `armsForCombination`) — so `A_B` → control + B
 * (`[50, 50]`), `A_B_C` → control + B + C (`[50, 25, 25]`), and `B_C` → B + C
 * (`[50, 50]`) with no control entry. Returns `null` before a combination is
 * chosen. Mirrors the Review step's arms-distribution copy.
 *
 * NOTE: this generalises the field's original control-first contract, which
 * assumed a control arm always occupied index 0. That holds for every
 * combination except `B_C` — see `docs/create-experiment-wizard.md` →
 * "Arm combinations".
 */
export function trafficSplitForCombination(combination: ArmCombination | null): number[] | null {
  if (combination === null) return null;
  return armsForCombination(combination).length === 3 ? [50, 25, 25] : [50, 50];
}

/** Count of non-control arms — what the Review step's "data cuts" reports. */
export function variantArmCountForCombination(combination: ArmCombination | null): number | null {
  return combination === null ? null : variantArmsForCombination(combination).length;
}

export type TestDuration = 28 | 45 | 60 | 90 | 180;

export const TEST_DURATION_OPTIONS: { value: TestDuration; label: string }[] = [
  { value: 28, label: '28 days' },
  { value: 45, label: '45 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '3 months' },
  { value: 180, label: '6 months' },
];

/** A single toggleable sub-item inside a content attribute section. */
export interface ContentSubAttribute {
  id: string;
  /** Stable wire key from the agent-services GMC catalog; persisted as the experiment's selection. */
  key: string;
  label: string;
}

export interface ContentAttribute {
  id: string;
  label: string;
  /** Short subtitle from the API (e.g. "Generated Copy", "Product Specifics"). */
  description: string;
  enabled: boolean;
  /** Individually toggleable sub-items displayed as checkbox pills when the section expands. */
  subAttributes: ContentSubAttribute[];
}

/**
 * One accordion's content-attribute selection: `selection[attrId][subId] = boolean`.
 * Parent "section checked" is derived from whether any sub-item is selected.
 */
export type ContentAttributeSelection = Record<string, Record<string, boolean>>;

/**
 * Step 4 — Experiment Strategy: form fields + one content-attribute selection per
 * enriched arm (B / C). Control (A) is never enriched, so it carries none.
 */
export interface Step4State {
  name: string;
  primaryKpi: string | null;
  secondaryKpi: string | null;
  /** Which arms the experiment runs. Pre-filled from the platform's Treatment
   *  Configuration and overridable per-experiment. */
  armCombination: ArmCombination | null;
  /** Treatment (enrichment schema) assigned to Arm B. Pre-filled from the
   *  platform's Treatment Configuration; overridable per-experiment. `null`
   *  until the treatment catalog / config resolves. Arm A is always Control
   *  (derived from the catalog) so it carries no id here. */
  armBTreatmentId: string | null;
  /** Treatment assigned to Arm C — only meaningful in a 3-arm (or A_C/B_C)
   *  combination; preserved across combination changes. */
  armCTreatmentId: string | null;
  testDuration: TestDuration | null;
  /** Per-arm selection. Both B and C are kept even when the combination runs
   *  one of them, so switching combinations never loses a selection — only the
   *  arms the final combination runs are sent. Empty until the screen seeds it. */
  armContentAttributes: Partial<Record<VariantArmId, ContentAttributeSelection>>;
  /** Optional post-enrichment steps for this experiment's runs. Chosen on this
   *  step beside the content attributes — both answer "what does this run
   *  generate" — and put on the wire by the review step at launch. */
  postStepFlags: EnrichmentPostStepFlags;
  /**
   * True when the DRAFT already carries an arm-assignment CSV, from
   * `armAssignmentFileUri` on resume.
   *
   * The file itself lives in a separate in-memory store and is lost on reload,
   * so without this the upload control renders empty on a resumed draft that
   * has one attached — and an operator who reads that as "no file" gets no
   * warning, because re-saving without a file doesn't clear the stored one.
   */
  hasStoredArmAssignmentFile: boolean;
  /** google-agent retailer prompt slug; `''` = the shared prompt. Set by `@lily.ai` users only. */
  retailer: string;
  /** Locales translated from the English; `[]` = English only. Set by `@lily.ai` users only. */
  targetLocales: string[];
}

// ---- Wizard-level --------------------------------------------------

export type WizardStep = 1 | 2 | 3 | 4 | 5;

/**
 * Channel context captured at the channel picker — both fields required.
 * `code` drives platform-scoped catalogs (e.g. KPI list) and `connectionId`
 * is sent to the experiment-service so the backend can associate the draft
 * with the right ad-account / GMC connection.
 */
export interface ExperimentChannel {
  connectionId: string;
  code: string;
}

export interface CreateExperimentWizardState {
  /**
   * Channel captured from the picker. `null` until the user picks a channel —
   * step 1 surfaces a missing-channel state in that case. Replaces the prior
   * `connectorCode`-only field so connection identity travels with the code,
   * removing the per-step `useConnectedConnectors` lookup.
   */
  channel: ExperimentChannel | null;
  /**
   * Draft experiment id returned by the server on step-1 completion. Anchors
   * all subsequent PATCH calls as later steps are wired up. Set once per
   * wizard session; cleared by `reset()` at wizard completion or by the
   * channel picker when a new wizard is started.
   */
  draftId: string | null;
  /**
   * Candidate-scope CSV the operator picked on Define Scope. Serves two jobs:
   *
   *  - it narrows the PREVIEW table through the by-ids-file route, which has no
   *    id-count cap, and
   *  - on a fresh wizard it rides the multipart create as `offerIdsFile`, since
   *    the draft doesn't exist yet when step 2 is open (the create only fires
   *    when `draftId` is null, so sending it whenever present is correct).
   *
   * NOT persisted (see `partialize`) — a `File` can't survive a JSON round-trip.
   * After a reload the preview falls back to the capped `uploadPreviewIds`.
   */
  candidateScopeFile: File | null;
  /**
   * Snapshot id captured from the first `/products/list` response (step 2,
   * step 3 as a fallback). Pinned to the draft via the step-2 PATCH so every
   * downstream operation (scoring, enrichment, pipeline) reads from the same
   * catalog ingestion. `null` until the first `/list` response arrives.
   */
  syncId: string | null;
  /**
   * Total SKU count of the step-2 scope (before any step-3 grouping), captured
   * on step 3 from the scope-only product-list count. Read on the review step
   * to derive the "All Other Products" segment count (scope total − group SKUs)
   * without an extra request. `null` until step 3 has resolved the count.
   */
  scopeSkuTotal: number | null;
  currentStep: WizardStep;
  completedSteps: number[];
  step1: Step1State;
  step2: Step2State;
  step3: Step3State;
  step4: Step4State;
}

// ---- API response types --------------------------------------------

export interface CatalogFilterOptions {
  channels: string[];
  countries: string[];
  availability: string[];
  creationTime: string[];
  dataSource: string[];
  expirationTime: string[];
  googleProductCategory: string[];
}

export interface PaginatedCatalog {
  data: CatalogProduct[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}
